import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  ConfirmModal,
  SuccessModal,
  ErrorModal,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Poll,
  Add,
  Analytics,
  Close,
  Warning,
  CheckCircle,
} from '../../mui-icons';
import './SurveysPage.css';
import { SURVEY_LIMITS } from '../../constants/surveyLimits';

const EMPTY_QUESTION = { prompt_text: '', help_text: '', sort_order: 0 };

function CharCounter({ value, max }) {
  const len = (value || '').length;
  return (
    <span className={`char-counter${len >= max ? ' char-counter--limit' : ''}`}>
      {len}/{max}
    </span>
  );
}

const SurveysPage = () => {
  const { canEdit } = useAdminAuth();
  const [view, setView] = useState('list');
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    internal_title: '',
    app_title: '',
    welcome_message: 'Queremos ouvir você! Sua opinião nos ajuda a melhorar o app.',
    thank_you_message: 'Obrigado pelo seu feedback!',
    expires_days: 7,
    questions: [{ ...EMPTY_QUESTION, sort_order: 0 }],
  });
  const [pushFilters, setPushFilters] = useState([]);
  const [recipientType, setRecipientType] = useState('filter');
  const [selectedFilter, setSelectedFilter] = useState('active_app_7d');
  const [selectedUserIds, setSelectedUserIds] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [publishForm, setPublishForm] = useState({
    send_push: true,
    push_title: '{first_name}, sua opinião importa!',
    push_body: 'Responda nossa pesquisa rápida no app (cerca de 1 min).',
    personalize: true,
  });
  const [publishing, setPublishing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [confirmManyQuestions, setConfirmManyQuestions] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, message: '', success: true });

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getSurveys({ limit: 50 });
      setSurveys(res.data?.surveys || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSurveys();
    adminAPI.getPushFilters().then((res) => {
      if (res.data?.filters) setPushFilters(res.data.filters);
    });
  }, [loadSurveys]);

  const questionCount = form.questions.length;
  const questionWarning =
    questionCount >= 6 ? 'error' : questionCount >= 4 ? 'warning' : 'ok';

  const getRecipientsConfig = () => {
    if (recipientType === 'all_with_token') return { type: 'all_with_token' };
    if (recipientType === 'filter') return { type: 'filter', filter: selectedFilter };
    const ids = selectedUserIds
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id) && id > 0);
    return { type: 'selected', user_ids: ids };
  };

  const countRecipients = async () => {
    setCountLoading(true);
    try {
      const res = await adminAPI.countSurveyRecipients(getRecipientsConfig());
      setRecipientCount({
        total: res.data?.total_recipients,
        description: res.data?.description,
      });
    } catch (e) {
      setRecipientCount({ total: 0, error: e.message });
    } finally {
      setCountLoading(false);
    }
  };

  const addQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { ...EMPTY_QUESTION, sort_order: prev.questions.length },
      ],
    }));
  };

  const removeQuestion = (index) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, sort_order: i })),
    }));
  };

  const updateQuestion = (index, field, value) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const saveDraft = async () => {
    if (!form.internal_title.trim() || !form.app_title.trim()) {
      setResultModal({ open: true, success: false, message: 'Preencha os títulos' });
      return null;
    }
    const validQuestions = form.questions.filter((q) => q.prompt_text.trim());
    if (validQuestions.length === 0) {
      setResultModal({ open: true, success: false, message: 'Adicione ao menos uma pergunta' });
      return null;
    }
    if (questionCount >= 6 && !confirmManyQuestions) {
      setConfirmManyQuestions(true);
      return null;
    }

    const payload = {
      ...form,
      questions: validQuestions.map((q, i) => ({ ...q, sort_order: i })),
    };

    try {
      let id = editingId;
      if (editingId) {
        await adminAPI.updateSurvey(editingId, payload);
      } else {
        const res = await adminAPI.createSurvey(payload);
        id = res.data?.survey?.id;
        setEditingId(id);
      }
      setResultModal({ open: true, success: true, message: 'Rascunho salvo!' });
      loadSurveys();
      return id;
    } catch (e) {
      setResultModal({ open: true, success: false, message: e.message });
      return null;
    }
  };

  const handlePublish = async () => {
    let surveyId = editingId;
    if (!surveyId) {
      surveyId = await saveDraft();
      if (!surveyId) return;
    }
    setPublishing(true);
    try {
      const res = await adminAPI.publishSurvey(surveyId, {
        recipients: getRecipientsConfig(),
        ...publishForm,
      });
      setResultModal({
        open: true,
        success: true,
        message: `Publicado! ${res.data?.assignments_created || 0} destinatários.`,
      });
      setView('list');
      setWizardStep(0);
      setEditingId(null);
      loadSurveys();
    } catch (e) {
      setResultModal({ open: true, success: false, message: e.message });
    } finally {
      setPublishing(false);
    }
  };

  const openAnalytics = async (id) => {
    setAnalyticsId(id);
    try {
      const res = await adminAPI.getSurveyAnalytics(id);
      setAnalytics(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const startCreate = () => {
    setEditingId(null);
    setForm({
      internal_title: '',
      app_title: '',
      welcome_message: 'Queremos ouvir você! Sua opinião nos ajuda a melhorar o app.',
      thank_you_message: 'Obrigado pelo seu feedback!',
      expires_days: 7,
      questions: [{ ...EMPTY_QUESTION, sort_order: 0 }],
    });
    setWizardStep(0);
    setView('wizard');
  };

  const editDraft = async (id) => {
    try {
      const res = await adminAPI.getSurvey(id);
      const s = res.data?.survey;
      if (!s) return;
      if (s.status !== 'draft') {
        openAnalytics(id);
        return;
      }
      setEditingId(id);
      setForm({
        internal_title: s.internal_title,
        app_title: s.app_title,
        welcome_message: s.welcome_message || '',
        thank_you_message: s.thank_you_message || '',
        expires_days: s.expires_days || 7,
        questions: s.questions?.length
          ? s.questions.map((q, i) => ({
              prompt_text: q.prompt_text,
              help_text: q.help_text || '',
              sort_order: i,
            }))
          : [{ ...EMPTY_QUESTION }],
      });
      setWizardStep(0);
      setView('wizard');
    } catch (e) {
      console.error(e);
    }
  };

  const filtersByGroup = pushFilters.reduce((acc, f) => {
    const g = f.group || 'Outros';
    if (!acc[g]) acc[g] = [];
    acc[g].push(f);
    return acc;
  }, {});

  if (view === 'wizard') {
    return (
      <div className="surveys-page">
        <div className="page-header">
          <Poll sx={{ fontSize: 40 }} />
          <div>
            <h1>{editingId ? 'Editar pesquisa' : 'Nova pesquisa'}</h1>
            <p>Passo {wizardStep + 1} de 4</p>
          </div>
          <button type="button" className="btn-secondary" onClick={() => setView('list')}>
            Voltar
          </button>
        </div>

        <div className="wizard-steps">
          {['Informações', 'Perguntas', 'Público', 'Publicar'].map((label, i) => (
            <button
              key={label}
              type="button"
              className={`wizard-step ${wizardStep === i ? 'active' : ''} ${wizardStep > i ? 'done' : ''}`}
              onClick={() => setWizardStep(i)}
            >
              {label}
            </button>
          ))}
        </div>

        {wizardStep === 0 && (
          <div className="admin-card pn-card pn-card--default">
            <label>
              Título interno (admin){' '}
              <CharCounter value={form.internal_title} max={SURVEY_LIMITS.INTERNAL_TITLE_MAX} />
            </label>
            <input
              value={form.internal_title}
              onChange={(e) => setForm({ ...form, internal_title: e.target.value })}
              placeholder="Ex: Satisfação v2.1 — março"
              maxLength={SURVEY_LIMITS.INTERNAL_TITLE_MAX}
            />
            <label>
              Título no app (header){' '}
              <CharCounter value={form.app_title} max={SURVEY_LIMITS.APP_TITLE_MAX} />
            </label>
            <input
              value={form.app_title}
              onChange={(e) => setForm({ ...form, app_title: e.target.value })}
              placeholder="Ex: Como está sua experiência?"
              maxLength={SURVEY_LIMITS.APP_TITLE_MAX}
            />
            <p className="field-hint">
              Máximo {SURVEY_LIMITS.APP_TITLE_MAX} caracteres — exibido no header verde do app em uma linha.
            </p>
            <label>
              Mensagem de boas-vindas{' '}
              <CharCounter value={form.welcome_message} max={SURVEY_LIMITS.WELCOME_MESSAGE_MAX} />
            </label>
            <textarea
              value={form.welcome_message}
              onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
              rows={3}
              maxLength={SURVEY_LIMITS.WELCOME_MESSAGE_MAX}
            />
            <label>
              Mensagem de agradecimento{' '}
              <CharCounter value={form.thank_you_message} max={SURVEY_LIMITS.THANK_YOU_MESSAGE_MAX} />
            </label>
            <textarea
              value={form.thank_you_message}
              onChange={(e) => setForm({ ...form, thank_you_message: e.target.value })}
              rows={2}
              maxLength={SURVEY_LIMITS.THANK_YOU_MESSAGE_MAX}
            />
            <label>Dias até expirar</label>
            <input
              type="number"
              min={1}
              max={90}
              value={form.expires_days}
              onChange={(e) => setForm({ ...form, expires_days: parseInt(e.target.value, 10) || 7 })}
            />
          </div>
        )}

        {wizardStep === 1 && (
          <div className="admin-card pn-card pn-card--default">
            <div className={`question-count-banner ${questionWarning}`}>
              {questionWarning === 'ok' && <CheckCircle />}
              {questionWarning !== 'ok' && <Warning />}
              <span>
                {questionCount} pergunta(s) —{' '}
                {questionCount <= 3
                  ? 'Ideal para push'
                  : questionCount <= 5
                    ? 'Pode reduzir taxa de conclusão'
                    : 'Muitas perguntas — confirme antes de publicar'}
              </span>
            </div>
            {form.questions.map((q, index) => (
              <div className="question-block" key={index}>
                <label>
                  Pergunta {index + 1}{' '}
                  <CharCounter value={q.prompt_text} max={SURVEY_LIMITS.QUESTION_PROMPT_MAX} />
                </label>
                <input
                  value={q.prompt_text}
                  onChange={(e) => updateQuestion(index, 'prompt_text', e.target.value)}
                  placeholder="Ex: O app atende suas expectativas?"
                  maxLength={SURVEY_LIMITS.QUESTION_PROMPT_MAX}
                />
                <label>
                  Texto de ajuda (opcional){' '}
                  <CharCounter value={q.help_text} max={SURVEY_LIMITS.QUESTION_HELP_MAX} />
                </label>
                <input
                  value={q.help_text}
                  onChange={(e) => updateQuestion(index, 'help_text', e.target.value)}
                  placeholder="Ex: Avalie a última semana de uso"
                  maxLength={SURVEY_LIMITS.QUESTION_HELP_MAX}
                />
                {form.questions.length > 1 && (
                  <button type="button" className="btn-danger-sm" onClick={() => removeQuestion(index)}>
                    Remover
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn-secondary" onClick={addQuestion}>
              + Adicionar pergunta
            </button>
          </div>
        )}

        {wizardStep === 2 && (
          <div className="admin-card pn-card pn-card--default">
            <label>Tipo de público</label>
            <select value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
              <option value="filter">Filtro</option>
              <option value="all_with_token">Todos os usuários</option>
              <option value="selected">IDs selecionados</option>
            </select>
            {recipientType === 'filter' && (
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                {Object.entries(filtersByGroup).map(([group, filters]) => (
                  <optgroup key={group} label={group}>
                    {filters.map((f) => (
                      <option key={f.filter} value={f.filter}>
                        {f.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
            {recipientType === 'selected' && (
              <input
                value={selectedUserIds}
                onChange={(e) => setSelectedUserIds(e.target.value)}
                placeholder="IDs separados por vírgula"
              />
            )}
            <button type="button" className="btn-secondary" onClick={countRecipients} disabled={countLoading}>
              {countLoading ? 'Contando...' : 'Contar destinatários'}
            </button>
            {recipientCount && (
              <p className="recipient-count">
                {recipientCount.error
                  ? recipientCount.error
                  : `${recipientCount.total} — ${recipientCount.description}`}
              </p>
            )}
          </div>
        )}

        {wizardStep === 3 && (
          <div className="admin-card pn-card pn-card--default">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={publishForm.send_push}
                onChange={(e) => setPublishForm({ ...publishForm, send_push: e.target.checked })}
              />
              Enviar push agora
            </label>
            {publishForm.send_push && (
              <>
                <label>
                  Título do push{' '}
                  <CharCounter value={publishForm.push_title} max={SURVEY_LIMITS.PUSH_TITLE_MAX} />
                </label>
                <input
                  value={publishForm.push_title}
                  onChange={(e) => setPublishForm({ ...publishForm, push_title: e.target.value })}
                  maxLength={SURVEY_LIMITS.PUSH_TITLE_MAX}
                />
                <label>
                  Corpo do push{' '}
                  <CharCounter value={publishForm.push_body} max={SURVEY_LIMITS.PUSH_BODY_MAX} />
                </label>
                <textarea
                  value={publishForm.push_body}
                  onChange={(e) => setPublishForm({ ...publishForm, push_body: e.target.value })}
                  rows={3}
                  maxLength={SURVEY_LIMITS.PUSH_BODY_MAX}
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={publishForm.personalize}
                    onChange={(e) => setPublishForm({ ...publishForm, personalize: e.target.checked })}
                  />
                  Personalizar com {'{first_name}'}
                </label>
              </>
            )}
            <p className="hint">
              Salve o rascunho antes de publicar. Usuários com opt-out de marketing não recebem push.
            </p>
          </div>
        )}

        <div className="wizard-actions">
          {wizardStep > 0 && (
            <button type="button" className="btn-secondary" onClick={() => setWizardStep(wizardStep - 1)}>
              Anterior
            </button>
          )}
          {canEdit() && wizardStep < 3 && (
            <button type="button" className="btn-primary" onClick={() => setWizardStep(wizardStep + 1)}>
              Próximo
            </button>
          )}
          {canEdit() && (
            <button type="button" className="btn-secondary" onClick={saveDraft}>
              Salvar rascunho
            </button>
          )}
          {canEdit() && wizardStep === 3 && (
            <button type="button" className="btn-primary" onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publicando...' : 'Publicar pesquisa'}
            </button>
          )}
        </div>

        <ConfirmModal
          open={confirmManyQuestions}
          title="Muitas perguntas"
          message="Pesquisas com mais de 5 perguntas costumam ter baixa taxa de conclusão. Deseja continuar?"
          confirmLabel="Sim, continuar"
          cancelLabel="Revisar perguntas"
          onClose={() => setConfirmManyQuestions(false)}
          onConfirm={() => {
            setConfirmManyQuestions(false);
            saveDraft();
          }}
        />

        <SuccessModal
          open={resultModal.open && resultModal.success}
          message={resultModal.message}
          onClose={() => setResultModal({ open: false })}
        />

        <ErrorModal
          open={resultModal.open && !resultModal.success}
          message={resultModal.message}
          onClose={() => setResultModal({ open: false })}
        />
      </div>
    );
  }

  return (
    <div className="surveys-page">
      <div className="page-header">
        <Poll sx={{ fontSize: 40 }} />
        <div>
          <h1>Pesquisas de Satisfação</h1>
          <p>CSAT por estrelas — push e in-app</p>
        </div>
        {canEdit() && (
          <button type="button" className="btn-primary" onClick={startCreate}>
            <Add /> Nova pesquisa
          </button>
        )}
      </div>

      {loading ? (
        <>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={40} style={{ marginBottom: 8 }} />
          ))}
        </>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Título</th>
                <th>Status</th>
                <th>Perguntas</th>
                <th>Destinatários</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{s.internal_title}</td>
                  <td>
                    <span className={`status-badge status-${s.status}`}>{s.status}</span>
                  </td>
                  <td>{s.question_count}</td>
                  <td>{s.assignment_count || 0}</td>
                  <td>
                    <button type="button" className="btn-icon" onClick={() => openAnalytics(s.id)} title="Analytics">
                      <Analytics />
                    </button>
                    {s.status === 'draft' && canEdit() && (
                      <button type="button" className="btn-icon" onClick={() => editDraft(s.id)}>
                        Editar
                      </button>
                    )}
                    {s.status === 'published' && canEdit() && (
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={async () => {
                          await adminAPI.closeSurvey(s.id);
                          loadSurveys();
                        }}
                      >
                        Encerrar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {analytics && (
        <FormModal
          open
          onClose={() => setAnalytics(null)}
          title={`Analytics — ${analytics.survey?.internal_title || analyticsId}`}
          large
          wide
        >
          <div className="stats-grid">
            <div className="stat-card pn-card pn-card--stat">
              <span>Total</span>
              <strong>{analytics.stats?.total_assignments}</strong>
            </div>
            <div className="stat-card pn-card pn-card--stat">
              <span>Concluídos</span>
              <strong>{analytics.stats?.completed}</strong>
            </div>
            <div className="stat-card pn-card pn-card--stat">
              <span>Taxa conclusão</span>
              <strong>{analytics.stats?.completion_rate_percent}%</strong>
            </div>
            <div className="stat-card pn-card pn-card--stat">
              <span>Dispensados</span>
              <strong>{analytics.stats?.dismissed}</strong>
            </div>
          </div>
          <h3>Por pergunta</h3>
          <ul>
            {analytics.questions?.map((q) => (
              <li key={q.id}>
                {q.prompt_text} — média: {q.avg_rating ?? '-'} ({q.answer_count} respostas)
              </li>
            ))}
          </ul>
          {analytics.negative_comments?.length > 0 && (
            <>
              <h3>Comentários 1–2 estrelas</h3>
              <ul className="negative-list">
                {analytics.negative_comments.map((c, i) => (
                  <li key={i}>
                    <strong>{c.rating}★</strong> {c.user_name}: {c.comment}
                  </li>
                ))}
              </ul>
            </>
          )}
        </FormModal>
      )}
    </div>
  );
};

export default SurveysPage;
